const PAYMENT_FREQUENCY_ENUM = {
  oneTime: 1,
  month: 2,
  year: 3,
}
const PERIODICITY = {
  [PAYMENT_FREQUENCY_ENUM.month]: 'month',
  [PAYMENT_FREQUENCY_ENUM.year]: 'year',
}
const PRICING_MODEL_ENUM = {
  flatRate: 1,
  perUser: 2,
  perFeature: 3,
  usageBased: 4,
  other: 5,
}
const PRICING_MODEL = {
  [PRICING_MODEL_ENUM.flatRate]: 'Flat Rate',
  [PRICING_MODEL_ENUM.perUser]: 'Per User',
  [PRICING_MODEL_ENUM.perFeature]: 'Per Feature',
  [PRICING_MODEL_ENUM.usageBased]: 'Usage Based',
}

const PAYMENT_FREQUENCY_MAP = {
  [PAYMENT_FREQUENCY_ENUM.oneTime]: 'One-time payment',
  [PAYMENT_FREQUENCY_ENUM.month]: 'Per month',
  [PAYMENT_FREQUENCY_ENUM.year]: 'Per year',
}

const PRICING_MODEL_MAP = {
  [PRICING_MODEL_ENUM.flatRate]: { icon: ['fal', 'user-circle'], text: 'Flat Rate' },
  [PRICING_MODEL_ENUM.perFeature]: { icon: ['far', 'shapes'], text: 'Per Feature' },
  [PRICING_MODEL_ENUM.usageBased]: { icon: ['far', 'signal'], text: 'Usage Based' },
  [PRICING_MODEL_ENUM.other]: { icon: ['far', 'signal'], text: 'Other' },
}

const getStartingPricePlan = plans =>
  plans.filter(plan => !!plan.startingPrice).sort((a, b) => a.startingPrice - b.startingPrice)[0] || null

const getCategories = ({ pricing, licensingModel = null }) => {
  const categories = []
  if (pricing.hasFreeTrial) categories.push('free-trial')
  if (pricing.hasFreeVersion) categories.push('free')
  if (pricing.plans.some(plan => plan.paymentFrequency === 2 || plan.paymentFrequency === 3))
    categories.push('subscription')
  if (pricing.plans.some(plan => plan.paymentFrequency === 1)) categories.push('one-time-license')
  if (!!licensingModel && licensingModel?.id === 2) categories.push('open-source')
  return categories
}

const formatData = listing => {
  const startingPricePlan = getStartingPricePlan(listing.pricing.plans)
  return {
    categories: getCategories({ pricing: listing.pricing, licensingModel: listing.licensingModel }),
    currency: listing.pricing.currency.code,
    currency_symbol: listing.pricing.currency.symbol,
    amount: startingPricePlan?.startingPrice?.toString() || null,
    periodicity: PERIODICITY[startingPricePlan?.paymentFrequency] || null,
    pricing_model: PRICING_MODEL[startingPricePlan?.pricingModel] || null,
    no_credit_card_required: !listing.pricing.creditCardRequired,
    price_range: listing.pricing.priceRange,
    plans: listing.pricing?.plans,
  }
}

const testUnusedCleanup = ({ pricingData, ...otherProps }) => {
  // eslint-disable-next-line no-unused-vars
  const { categories, ...listing } = pricingData.listing

  return {
    ...otherProps,
    pricingData: {
      ...pricingData,
      listing: {
        ...listing,
      },
    },
  }
}

/**
 * Maps pricing categories to boolean flags
 * @param {Array<string>} categories - Array of pricing categories
 * @returns {{hasFreeTrial: boolean, hasSubscription: boolean, isFree: boolean, isPaid: boolean}} Object with boolean flags for each pricing category
 */
const mapPricingCategories = (categories = []) => {
  const normalizedCategories = Array.isArray(categories) ? categories : []
  return {
    hasFreeTrial: normalizedCategories.includes('free-trial'),
    hasSubscription: normalizedCategories.includes('subscription'),
    isFree: normalizedCategories.includes('free'),
    isPaid: normalizedCategories.includes('paid'),
  }
}

/**
 * @param   {'EUR' | 'USD' | 'GBP' | 'YEN'} currency - Currency code
 * @returns {string} Icon name
 */
const mapCurrencyToIcon = currency => {
  switch (currency) {
    case 'USD':
      return 'dollar-sign'
    case 'EUR':
      return 'euro-sign'
    case 'GBP':
      return 'pound-sign'
    default:
      return 'dollar-sign'
  }
}

/**
 * Generates text representations for pricing model and periodicity
 * @param {Object} pricing - Pricing object containing periodicity and pricing_model
 * @returns {{periodicityText: string, pricingModelText: string}} Object with text for periodicity and pricing model
 */
const getPricingTexts = pricing => {
  const periodicityText = pricing?.periodicity
    ? `${PAYMENT_FREQUENCY_MAP[PAYMENT_FREQUENCY_ENUM[pricing.periodicity]]}`.toLowerCase()
    : ''
  const pricingModelText = pricing?.pricing_model ? pricing.pricing_model.toLowerCase() : ''

  return { periodicityText, pricingModelText }
}

export default formatData

export {
  PERIODICITY,
  PAYMENT_FREQUENCY_ENUM,
  PRICING_MODEL_ENUM,
  PAYMENT_FREQUENCY_MAP,
  PRICING_MODEL_MAP,
  testUnusedCleanup,
  mapPricingCategories,
  mapCurrencyToIcon,
  getPricingTexts,
}
